// Import JS Modules
import TemplateStore from '../templates/template-store';
import { stickerStore, patternStore } from '../utils/stores';
import wkDataInterrogator from '../misc/wk-data-interrogator';
import { fetchFavourites } from '../utils/fetch-favourites';
import { themeStore } from 'stores/themes';
import { TYPES } from '../common/tray-base';
import templateInterrogator from '../misc/template-interrogator';
import prismItemInterrogator from '../misc/prism-item-interrogator';
import { usePrismItemStore } from 'stores/usePrismItemStore';

function designCode() {
  const prismItem = usePrismItemStore.getState().prismItem;
  return (prismItem && prismItem.design_code) || '';
}

function isCustomColor() {
  return designCode().indexOf('.customcolor') > 0;
}

function isCollage() {
  const code = designCode();
  return (
    code.indexOf('.collage') === code.length - 8 ||
    code.indexOf('.collage.') !== -1 ||
    code.indexOf('collage.') === 0
  );
}

async function hasEventsTray() {
  return (
    window.wkData &&
    isCalendar() &&
    (await usePrismItemStore.getState().setDayEventsSlots()).length > 0
  );
}

function isAlbum() {
  return wkDataInterrogator.isAlbum();
}

function isCalendar() {
  return wkDataInterrogator.isCalendar();
}

function isCanvas() {
  return (
    (wkData.product && wkData.product.type === 'framedcanvas') ||
    wkData.product.type === 'canvaswrap'
  );
}

function isMultiPageCardProduct() {
  const productType = 'wmcard';

  return wkData.product['max-pages'] > 1 && wkData.product.type === productType;
}

function isMultiPageStandardProduct() {
  const nonMultiPageProductTypes = [
    'album',
    'wmcard',
    'calendar',
    'businesscard',
  ];
  return (
    wkData.product['max-pages'] > 1 &&
    nonMultiPageProductTypes.every(
      (productType) => wkData.product.type !== productType,
    )
  );
}

function hasLayoutsTray() {
  const templateGroups = TemplateStore.list();
  if (!templateGroups) return false;

  const currentSection = prismItemInterrogator.getCurrentSection();
  if (currentSection === null) return false;

  const activeTemplate = window.activeView?.template;
  const orientationFilter =
    templateInterrogator.orientationFilter(activeTemplate);

  const availableTemplates = TemplateStore.templatesFiltered({
    section: currentSection,
    orientations: orientationFilter,
  });

  const hasMultipleTemplates = availableTemplates.length >= 2;

  return hasMultipleTemplates || isCollage() || isCustomColor();
}

function hasEdgesTray() {
  return (
    wkData.product.type === 'canvaswrap' &&
    wkData.vars.length === 0 &&
    TemplateStore.wraps()
  );
}

async function hasColourTray() {
  if (!window.activeView) return false;
  if (window.activeView.customPage) return false;

  if (
    window.activeView &&
    typeof activeView.hasOptionalBackground === 'function' &&
    activeView.hasOptionalBackground()
  ) {
    return true;
  }

  try {
    if (await hasStickersTray()) {
      return true;
    }
  } catch (error) {
    console.error('Error checking stickers tray:', error);
  }

  try {
    if (await hasPatternsTray()) {
      return true;
    }
  } catch (error) {
    console.error('Error checking patterns tray:', error);
  }
}

function hasWrapsTray() {
  return TemplateStore.hasWraps;
}

function* chicletVariables() {
  for (const variable of wkData.vars) {
    if (variable.builder_chiclet_option && variable.options.length > 1) {
      yield variable;
    }
  }
}

function* chicletCardVariables() {
  for (const variable of wkData.vars) {
    if (variable.builder_chiclet_option) {
      yield variable;
    }
  }
}

function chicletVariablesPrimary() {
  // there are no top level (have their own chiclets) if wkData.product_tray_enabled
  if (wkData.product_tray_enabled) {
    return [];
  }

  return Array.from(chicletVariables()).filter(variableWouldBePrimary);
}

function chicletVariablesSecondary() {
  const variables = Array.from(chicletVariables());

  return variables;
}

function findVariable(name) {
  return [...chicletVariables()].find((v) => v.name === name);
}

function findCardVariable(name) {
  return [...chicletCardVariables()].find((v) => v.name === name);
}

function hasVariablesTray() {
  return [...chicletVariablesSecondary()].length > 0;
}

function variableWouldBePrimary({ name: variableName }) {
  return ['frame', 'cover'].some((chicletName) =>
    variableName.match(new RegExp(chicletName, 'i')),
  );
}

async function hasFavouritesTray() {
  const favourites = await fetchFavourites();
  return favourites?.length > 0;
}

// Do NOT call themeStore.fetch() here — it dispatches a preliminary
// THEMES_LOADED (with an empty themes array) which re-triggers
// checkDesignsTray(), causing a dispatch loop that hits the MAX_DELAYED_DISPATCHES
// limit. If themes haven't arrived yet, return false; the THEMES_LOADED
// event will call checkDesignsTray() again once they are ready.
function hasThemesToShow() {
  const themes = themeStore.themes;
  if (themes.length === 0) return false;
  const currentCode = usePrismItemStore.getState().prismItem?.design_code;
  return themes.some((t) => t.design_code !== currentCode);
}

async function hasDesignsTray() {
  if (themeStore.themes.length === 0) return hasLayoutsTray();
  return hasThemesToShow() || hasLayoutsTray();
}

async function hasDesignsButNotLayoutTray() {
  return hasThemesToShow();
}

async function hasStickersTray() {
  return await stickerStore.some();
}

async function hasPatternsTray() {
  return await patternStore.some();
}

function isPartOfDesignTray(activeTray) {
  const validTypes = [TYPES.text, TYPES.photos, TYPES.colours];

  return validTypes.includes(activeTray);
}

function hasMultipleOrientations() {
  const currentSection = prismItemInterrogator.getCurrentSection();
  if (currentSection === null) return false;

  const orientationFilters = new Set(
    templateInterrogator.possibleOrientationFilters(),
  );
  const found = new Set();

  const templates = TemplateStore.templatesFiltered({
    section: currentSection,
  });

  return templates.some((template) =>
    (template.tags || []).some((tag) => {
      if (orientationFilters.has(tag)) {
        found.add(tag);
        return found.size > 1;
      }
      return false;
    }),
  );
}

export {
  hasFavouritesTray,
  hasDesignsTray,
  hasLayoutsTray,
  hasDesignsButNotLayoutTray,
  hasEdgesTray,
  hasColourTray,
  hasEventsTray,
  hasMultipleOrientations,
  isAlbum,
  isCalendar,
  isCanvas,
  isCollage,
  isMultiPageCardProduct,
  isMultiPageStandardProduct,
  chicletVariables,
  chicletCardVariables,
  chicletVariablesPrimary,
  chicletVariablesSecondary,
  findVariable,
  findCardVariable,
  hasVariablesTray,
  hasWrapsTray,
  hasStickersTray,
  hasPatternsTray,
  isPartOfDesignTray,
};
